import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import GlassCard from "../components/GlassCard";
import GoldButton from "../components/GoldButton";

export default function Login() {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) {
      setError(
        res.status === 401
          ? t("auth.errorInvalid")
          : res.error || t("auth.errorGeneric"),
      );
      return;
    }
    navigate("/dashboard", { replace: true });
  };

  return (
    <section className="mx-auto flex max-w-md flex-col gap-6 pt-10">
      <GlassCard className="p-8">
        <h1
          className="font-display text-3xl text-white tracking-tight"
          data-testid="login-title"
        >
          {t("auth.loginTitle")}
        </h1>
        <p className="mt-2 text-sm text-white/60">{t("auth.loginSubtitle")}</p>

        <form
          onSubmit={onSubmit}
          className="mt-7 grid gap-4"
          data-testid="login-form"
        >
          <Field label={t("auth.email")}>
            <input
              type="email"
              required
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="nxa-input"
            />
          </Field>
          <Field label={t("auth.password")}>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password-input"
              className="nxa-input"
            />
          </Field>

          {error && (
            <div
              className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200"
              data-testid="login-error"
            >
              {error}
            </div>
          )}

          <GoldButton
            type="submit"
            disabled={loading}
            className="mt-2 w-full"
            data-testid="login-submit-btn"
          >
            {loading ? t("common.loading") : t("auth.submitLogin")}
          </GoldButton>

          <p className="mt-3 text-center text-sm text-white/55">
            {t("auth.noAccount")}{" "}
            <Link
              to="/register"
              className="text-[#D4AF37] hover:text-[#F4D77A] transition-colors"
              data-testid="link-register"
            >
              {t("auth.createOne")}
            </Link>
          </p>
        </form>
      </GlassCard>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="grid gap-2 text-sm">
      <span className="text-white/65 font-medium">{label}</span>
      {children}
    </label>
  );
}
